import React, { useState } from 'react';
import s from '../DietPlanBuilder.module.css';
import { CheckRow, Sheet } from '../ui';
import { copy } from '../../copy';
import { firstName, fmtDate, fmtInt } from '../../lib/format';
import type { Builder, Dispatch } from '../../hooks/useDietPlanBuilder';
import type { Patient, Plan } from '../../types';

export function AssignSheet({ plan, patient, derived, state, dispatch, onAssign, onClose }: {
  plan: Plan; patient: Patient; derived: NonNullable<Builder['derived']>; state: Builder['s']; dispatch: Dispatch; onAssign: () => Promise<unknown>; onClose: () => void;
}) {
  const first = firstName(patient.name);
  const { issues, blocked, empties, shareSum, offOpts, totalOpts, active, tipsOverLimit } = derived;
  const hasIssues = issues.length > 0 || shareSum !== 100 || tipsOverLimit;
  const disabled = blocked || (hasIssues && !blocked && (!state.confirm || !state.reason.trim()));
  const [busy, setBusy] = useState(false);
  const patch = (p: { confirm?: boolean; reason?: string }) => dispatch({ type: 'assignField', patch: p });
  const list = [
    ...(tipsOverLimit ? [{ name: 'Prep tips', text: copy.meals.tipsOverLimitFooter }] : []),
    ...(shareSum !== 100
      ? [...issues.map(i => ({ name: i.name, text: copy.assign.mealIssue(i.bad, i.all, i.empty) })), { name: 'Meal split', text: copy.assign.splitIssue(shareSum) }]
      : issues.map(i => ({ name: i.name, text: copy.assign.mealIssue(i.bad, i.all, i.empty) }))),
  ];

  return (
    <Sheet title={copy.assign.title} sub={copy.assign.sub(first)} onClose={onClose}
      footer={<button type="button" className={s.btnPrimary} disabled={disabled || busy} onClick={async () => { setBusy(true); try { await onAssign(); } finally { setBusy(false); } }}>{busy ? '…' : issues.length && !blocked ? copy.assign.anyway : copy.assign.button}</button>}>
      <div className={s.sheetBody} style={{ gap: 12 }}>
        <div className={s.card} style={{ gap: 6, borderRadius: 16 }}>
          <div className={s.strong}>{plan.name}</div>
          <div className={s.muted}>{fmtDate(plan.from)} – {fmtDate(plan.to)}</div>
          <div className={s.muted}>{fmtInt(plan.kcal)} kcal · {active.length} meals · {totalOpts} options</div>
        </div>
        {hasIssues && (
          <div className={s.col} style={{ background: blocked ? 'var(--gf-red-bg)' : 'var(--gf-amber-bg)', borderRadius: 14, padding: '12px 14px', gap: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: blocked ? 'var(--gf-red-ink)' : 'var(--gf-amber-ink)' }}>{blocked ? copy.assign.fix : copy.assign.off(offOpts)}</div>
            <div className={s.col} style={{ gap: 3 }}>{list.map((l, i) => <div key={i} style={{ fontSize: 12.5, color: '#4A4239' }}><b style={{ color: 'var(--gf-ink)' }}>{l.name}:</b> {l.text}</div>)}</div>
            <div style={{ fontSize: 12, color: blocked ? 'var(--gf-red-ink)' : 'var(--gf-amber-ink)' }}>{blocked ? (tipsOverLimit ? copy.meals.tipsOverLimitFooter : empties ? copy.assign.emptyNote(first) : copy.assign.splitNote) : copy.assign.offNote}</div>
            {!blocked && (
              <>
                <CheckRow on={state.confirm} onChange={v => patch({ confirm: v })}><span style={{ fontSize: 13, fontWeight: 600 }}>{copy.assign.confirm}</span></CheckRow>
                {state.confirm && (
                  <div className={s.col}>
                    <div className={s.strong} style={{ fontSize: 12 }}>{copy.assign.why}</div>
                    <textarea className={s.textarea} rows={3} value={state.reason} onChange={e => patch({ reason: e.target.value })} placeholder={copy.assign.whyPh} />
                    <div className={s.faint} style={{ fontSize: 11 }}>{copy.assign.needed}</div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </Sheet>
  );
}

export function DiscardSheet({ onDiscard, onClose }: { onDiscard: () => void; onClose: () => void }) {
  return (
    <Sheet title={copy.leave.title} onClose={onClose}>
      <div className={s.sheetBody} style={{ gap: 10, paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' }}>
        <div style={{ fontSize: 13.5, color: '#4A4239', lineHeight: 1.5 }}>{copy.leave.body}</div>
        <div className={s.leaveActions}>
          <button type="button" className={s.btnOutlineGreen} onClick={onDiscard}>{copy.leave.discard}</button>
          <button type="button" className={s.btnSolidGreen} onClick={onClose}>{copy.leave.keep}</button>
        </div>
      </div>
    </Sheet>
  );
}
